import { useState } from "react";
import type { DlqEntrySummary } from "../../api/admin-dlq";
import { PageHeader } from "../../components/PageHeader";
import {
  ErrorBanner,
  LoadingIndicator,
  tableStyles as ts,
} from "../../components/TableParts";
import { t } from "../../i18n";
import { DlqDetailPanel } from "./DlqDetailPanel";
import { DlqField } from "./DlqField";
import { DLQ_QUEUES } from "./dlq-constants";
import { formatBytes, formatDlqDate, shortUlid } from "./dlq-formatters";
import { styles } from "./dlq-styles";
import {
  type DlqFilters,
  type DlqListState,
  INITIAL_DLQ_FILTERS,
  useDlqList,
} from "./use-dlq-list";

export function DlqPage(): React.JSX.Element {
  const [filters, setFilters] = useState<DlqFilters>(INITIAL_DLQ_FILTERS);
  const list = useDlqList(filters);

  return (
    <div>
      <PageHeader title={t("dlq.title")} description={t("dlq.description")} />
      <DlqControls filters={filters} setFilters={setFilters} list={list} />
      {list.error && <ErrorBanner message={list.error} />}
      <DlqContent list={list} />
    </div>
  );
}

function DlqControls({
  filters,
  setFilters,
  list,
}: {
  filters: DlqFilters;
  setFilters: (filters: DlqFilters) => void;
  list: DlqListState;
}): React.JSX.Element {
  return (
    <form
      style={styles.controls}
      onSubmit={(event) => {
        event.preventDefault();
        void list.load(null);
      }}
    >
      <QueueField filters={filters} setFilters={setFilters} />
      <DateField
        field="fromDate"
        label={t("dlq.from_date")}
        filters={filters}
        setFilters={setFilters}
      />
      <DateField
        field="toDate"
        label={t("dlq.to_date")}
        filters={filters}
        setFilters={setFilters}
      />
      <button type="submit" disabled={list.loading} style={ts.searchBtn}>
        {t("dlq.search")}
      </button>
    </form>
  );
}

function QueueField({
  filters,
  setFilters,
}: {
  filters: DlqFilters;
  setFilters: (filters: DlqFilters) => void;
}): React.JSX.Element {
  return (
    <DlqField label={t("dlq.origin_queue")} htmlFor="dlq-origin-queue">
      <select
        id="dlq-origin-queue"
        value={filters.originQueue}
        style={styles.select}
        onChange={(event) =>
          setFilters({ ...filters, originQueue: event.target.value })
        }
      >
        {DLQ_QUEUES.map((queue) => (
          <option key={queue} value={queue}>
            {queue}
          </option>
        ))}
      </select>
    </DlqField>
  );
}

function DateField({
  field,
  label,
  filters,
  setFilters,
}: {
  field: "fromDate" | "toDate";
  label: string;
  filters: DlqFilters;
  setFilters: (filters: DlqFilters) => void;
}): React.JSX.Element {
  return (
    <DlqField label={label} htmlFor={`dlq-${field}`}>
      <input
        id={`dlq-${field}`}
        type="date"
        value={filters[field]}
        style={styles.input}
        onChange={(event) =>
          setFilters({ ...filters, [field]: event.target.value })
        }
      />
    </DlqField>
  );
}

function DlqContent({
  list,
}: {
  list: DlqListState;
}): React.JSX.Element {
  if (list.loading) return <LoadingIndicator />;
  if (!list.searched) return <p style={styles.hint}>{t("dlq.initial_hint")}</p>;
  if (list.items.length === 0)
    return <p style={ts.emptyText}>{t("dlq.empty")}</p>;
  return <DlqTable list={list} />;
}

function DlqTable({
  list,
}: {
  list: DlqListState;
}): React.JSX.Element {
  return (
    <>
      <table style={ts.table}>
        <thead>
          <tr>
            <th style={ts.th}>{t("dlq.col_entry")}</th>
            <th style={ts.th}>{t("dlq.col_queue")}</th>
            <th style={ts.th}>{t("dlq.col_attempts")}</th>
            <th style={ts.th}>{t("dlq.col_error")}</th>
            <th style={ts.th}>{t("dlq.col_size")}</th>
            <th style={ts.th}>{t("dlq.col_replay")}</th>
            <th style={ts.th}>{t("dlq.col_uploaded")}</th>
          </tr>
        </thead>
        <tbody>
          {list.items.map((entry) => (
            <DlqRow key={entry.key} entry={entry} list={list} />
          ))}
        </tbody>
      </table>
      {list.cursor && (
        <button
          type="button"
          style={ts.pageBtn}
          onClick={() => void list.load(list.cursor)}
        >
          {t("dlq.next_page")}
        </button>
      )}
    </>
  );
}

function DlqRow({
  entry,
  list,
}: {
  entry: DlqEntrySummary;
  list: DlqListState;
}): React.JSX.Element {
  const expanded = list.expandedKey === entry.key;
  return (
    <>
      <tr
        style={{ ...ts.row, cursor: "pointer" }}
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => list.setExpandedKey(expanded ? null : entry.key)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            list.setExpandedKey(expanded ? null : entry.key);
          }
        }}
      >
        <td style={ts.td}>
          <div style={styles.mono}>{shortUlid(entry.ulid)}</div>
          <div style={styles.meta}>{entry.partition_date}</div>
        </td>
        <td style={ts.td}>{entry.origin_queue}</td>
        <td style={ts.td}>{entry.attempts}</td>
        <td style={ts.td}>{entry.last_error_class}</td>
        <td style={ts.td}>{formatBytes(entry.body_bytes)}</td>
        <td style={ts.td}>
          <ReplayState entry={entry} />
        </td>
        <td style={ts.td}>{formatDlqDate(entry.uploaded_at)}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} style={ts.expandedCell}>
            <DlqDetailPanel
              entry={entry}
              onReplayDone={() => void list.load(null)}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function ReplayState({
  entry,
}: {
  entry: DlqEntrySummary;
}): React.JSX.Element {
  if (!entry.replayed_at) {
    return <span style={styles.statusMuted}>{t("dlq.not_replayed")}</span>;
  }
  return (
    <span style={styles.statusOk}>{formatDlqDate(entry.replayed_at)}</span>
  );
}
